import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import type { AuthResponse } from '../services/types';
import { API_BASE_URL, registerRefresh, registerUnauthorized } from '../services/http';
import type { UserDTO } from '../services/users';
import { Spin } from 'antd';

/* --------------------------- Tipi del Context --------------------------- */
interface AuthContextType {
  user: UserDTO | null;
  accessToken: string | null;
  refreshToken: string | null;
  login: (email: string, password: string) => Promise<UserDTO>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<boolean>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/* --------------------------- Token Manager ------------------------------ */
/** Token storage con persistenza su localStorage */
class TokenManager {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private readonly STORAGE_KEY = 'authTokens';

  constructor() {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        this.accessToken  = parsed?.accessToken  ?? null;
        this.refreshToken = parsed?.refreshToken ?? null;
      }
    } catch {
      /* ignore */
    }
  }

  getAccess()  { return this.accessToken; }
  getRefresh() { return this.refreshToken; }

  set(access: string, refresh: string) {
    this.accessToken  = access;
    this.refreshToken = refresh;
    localStorage.setItem(
      this.STORAGE_KEY,
      JSON.stringify({ accessToken: access, refreshToken: refresh })
    );
  }

  clear() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem(this.STORAGE_KEY);
  }
}

/** Esportato per il wrapper http (Authorization header) */
export const tokenManager = new TokenManager();

/* --------------------- Bridge compat: authService ----------------------- */
let _bridge: AuthContextType | null = null;

export const authService = {
  async login(credentials: { email: string; password: string }): Promise<UserDTO> {
    if (!_bridge) throw new Error('AuthProvider non montato');
    return _bridge.login(credentials.email, credentials.password);
  },
  async logout(): Promise<void> {
    if (!_bridge) throw new Error('AuthProvider non montato');
    return _bridge.logout();
  },
  async refreshToken(): Promise<boolean> {
    if (!_bridge) throw new Error('AuthProvider non montato');
    return _bridge.refreshAuth();
  },
  getCurrentUser(): UserDTO | null {
    return _bridge?.user ?? null;
  },
  hasRole(role: string): boolean {
    const r = _bridge?.user?.role || '';
    return r.toUpperCase() === role.toUpperCase();
  },
  isAuthenticated(): boolean {
    return !!_bridge?.accessToken && !!_bridge?.user;
  },
};

/* --------------------------- API Base/Root ------------------------------ */
/** Usa la stessa base URL del wrapper HTTP per evitare discrepanze. */
const buildAuthUrl = (path: string) => {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${suffix}`;
};

/* ------------------------- Persistenza utente --------------------------- */
const USER_STORAGE_KEY = 'authUser';

function loadUserFromStorage<T = any>(): T | null {
  try { return JSON.parse(localStorage.getItem(USER_STORAGE_KEY) || 'null'); }
  catch { return null; }
}
function saveUserToStorage(u: any | null) {
  if (u) localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(u));
  else localStorage.removeItem(USER_STORAGE_KEY);
}

/* ------------------------------- Provider ------------------------------- */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserDTO | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const login = useCallback(
    async (email: string, password: string): Promise<UserDTO> => {
      const res = await fetch(buildAuthUrl('/g/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || 'Login fallito');
      }

      const data: AuthResponse = await res.json();

      // salva token
      tokenManager.set(data.accessToken, data.refreshToken);
      setAccessToken(data.accessToken);
      setRefreshToken(data.refreshToken);

      // usa direttamente l'utente della risposta
      const u = data.user as unknown as UserDTO;
      setUser(u);
      saveUserToStorage(u);

      return u;
    },
    []
  );

  const logout = useCallback(async (): Promise<void> => {
    const token = tokenManager.getAccess();
    if (token) {
      try {
        await fetch(buildAuthUrl('/g/auth/logout'), {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        /* ignora errori di rete */
      }
    }
    tokenManager.clear();
    saveUserToStorage(null);
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
  }, []);

  const refreshAuth = useCallback(async (): Promise<boolean> => {
    const refresh = tokenManager.getRefresh();
    if (!refresh) return false;

    try {
      const res = await fetch(buildAuthUrl('/g/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: refresh }),
      });
      if (!res.ok) return false;

      const data: AuthResponse = await res.json();

      // salva token
      tokenManager.set(data.accessToken, data.refreshToken);
      setAccessToken(data.accessToken);
      setRefreshToken(data.refreshToken);

      // aggiorna utente da risposta
      const u = data.user as unknown as UserDTO;
      setUser(u);
      saveUserToStorage(u);

      return true;
    } catch {
      return false;
    }
  }, []);

  /* -------- Registra la funzione di refresh nel wrapper fetch -------- */
  useEffect(() => {
    registerRefresh(refreshAuth);
  }, [refreshAuth]);

  useEffect(() => {
    let active = true;
    const handleUnauthorized = () => {
      if (!active) return;
      void logout();
    };
    registerUnauthorized(handleUnauthorized);
    return () => {
      active = false;
      registerUnauthorized(null);
    };
  }, [logout]);

  /* ---------------- Bootstrap: ripristina sessione all'avvio ---------------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const access = tokenManager.getAccess();
        const refresh = tokenManager.getRefresh();
        const cachedUser = loadUserFromStorage<UserDTO>();

        if (access && cachedUser) {
          setAccessToken(access);
          setRefreshToken(refresh);
          if (!cancelled) setUser(cachedUser);
        } else if (refresh) {
          const ok = await refreshAuth();
          if (!ok && !cancelled) {
            tokenManager.clear();
            saveUserToStorage(null);
            setUser(null);
            setAccessToken(null);
            setRefreshToken(null);
          }
        } else {
          // nessun token -> utente non autenticato
          if (!cancelled) {
            setUser(null);
            setAccessToken(null);
            setRefreshToken(null);
          }
        }
      } catch {
        tokenManager.clear();
        saveUserToStorage(null);
        if (!cancelled) {
          setUser(null);
          setAccessToken(null);
          setRefreshToken(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshAuth]);

  const value: AuthContextType = {
    user,
    accessToken,
    refreshToken,
    login,
    logout,
    refreshAuth,
    isAuthenticated: !!accessToken && !!user,
  };

  // Mantieni aggiornato il bridge compat
  useEffect(() => {
    _bridge = value;
    return () => {
      _bridge = null;
    };
  }, [value]);

  if (loading) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
        }}
        aria-busy="true"
        aria-live="polite"
        role="status"
      >
        <Spin size="large" tip="Caricamento..." />
      </div>
    );
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/* ------------------------------- Hook ----------------------------------- */
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
